import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { Flower2, User, Baby, Calendar, Heart, Target, FlaskConical, FileText, Stethoscope } from "lucide-react";
import {
  BachFlowersSessionData,
  EMOTIONS_DOMINANTES_OPTIONS,
  createDefaultBachFlowersSession,
  prefillFromWelcomeQuestionnaire,
} from "@/types/bachFlowersSession";

interface BachFlower {
  id: string;
  name: string;
  description: string;
  main_effects: string[];
}

interface BachFlowersSessionFormProps {
  patientId: string;
  childId?: string | null;
  data: BachFlowersSessionData;
  onDataChange: (data: BachFlowersSessionData) => void;
}

export function BachFlowersSessionForm({ patientId, childId, data, onDataChange }: BachFlowersSessionFormProps) {
  const [bachFlowers, setBachFlowers] = useState<BachFlower[]>([]);
  const [loading, setLoading] = useState(true);
  // Radix Select n'accepte pas les value vides ("") pour les SelectItem.
  // On utilise une valeur sentinelle pour l'option "Aucune".
  const NONE_VALUE = "__none__";

  useEffect(() => {
    loadBachFlowers();
    loadQuestionnaireData();
  }, [patientId, childId]);

  const loadBachFlowers = async () => {
    try {
      const { data: flowers, error } = await supabase
        .from("bach_flowers")
        .select("id, name, description, main_effects")
        .order("name");

      if (error) throw error;
      setBachFlowers(flowers || []);
    } catch (error) {
      console.error("Error loading Bach flowers:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadQuestionnaireData = async () => {
    try {
      let questionnaire: any = null;

      // STRATEGY 1: Try to find questionnaire directly linked to patient
      const { data: directQuestionnaires, error: directError } = await supabase
        .from("welcome_questionnaires")
        .select("*")
        .eq("patient_id", patientId)
        .eq("questionnaire_type", "bach_flowers")
        .order("created_at", { ascending: false })
        .limit(1);

      if (!directError && directQuestionnaires && directQuestionnaires.length > 0) {
        questionnaire = directQuestionnaires[0];
        console.log("Found Bach Flowers questionnaire directly linked to patient:", questionnaire.id);
      }

      // STRATEGY 2: If no direct link, try via patient email and appointments
      if (!questionnaire) {
        const { data: patientData, error: patientError } = await supabase
          .from("patients")
          .select("email")
          .eq("id", patientId)
          .single();

        if (!patientError && patientData?.email) {
          // Find the most recent appointments for this patient by email
          const { data: appointments, error: aptError } = await supabase
            .from("appointments")
            .select("id")
            .ilike("email", patientData.email)
            .order("created_at", { ascending: false })
            .limit(5);

          if (!aptError && appointments && appointments.length > 0) {
            const appointmentIds = appointments.map(a => a.id);
            const { data: aptQuestionnaires, error } = await supabase
              .from("welcome_questionnaires")
              .select("*")
              .in("appointment_id", appointmentIds)
              .eq("questionnaire_type", "bach_flowers")
              .order("created_at", { ascending: false })
              .limit(1);

            if (!error && aptQuestionnaires && aptQuestionnaires.length > 0) {
              questionnaire = aptQuestionnaires[0];
              console.log("Found Bach Flowers questionnaire via email match:", questionnaire.id);
            }
          }
        }
      }

      if (questionnaire) {
        const responses = questionnaire.responses as Record<string, any>;
        const prefilled = prefillFromWelcomeQuestionnaire(responses);
        onDataChange({ ...data, ...prefilled });
      } else {
        console.log("No Bach Flowers questionnaire found for patient");
      }
    } catch (error) {
      console.error("Error loading questionnaire data:", error);
    }
  };

  const updateField = <K extends keyof BachFlowersSessionData>(
    field: K,
    value: BachFlowersSessionData[K]
  ) => {
    onDataChange({ ...data, [field]: value });
  };

  const toggleArrayField = (field: 'emotions_dominantes' | 'duree_ressenti' | 'impact_principal', value: string) => {
    const currentArray = data[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter((v) => v !== value)
      : [...currentArray, value];
    updateField(field, newArray);
  };

  const updateFleur = (key: keyof BachFlowersSessionData['fleurs_retenues'], value: string) => {
    const normalized = value === NONE_VALUE ? undefined : value;
    updateField('fleurs_retenues', { ...data.fleurs_retenues, [key]: normalized });
  };

  const DUREE_OPTIONS = [
    { id: 'jours', label: 'Quelques jours' },
    { id: 'semaines', label: 'Quelques semaines' },
    { id: 'mois', label: 'Plusieurs mois' },
    { id: 'incertain', label: 'Difficile à dire' },
  ];

  const IMPACT_OPTIONS = [
    { id: 'sommeil', label: 'Le sommeil' },
    { id: 'stress', label: 'Le stress / l\'anxiété' },
    { id: 'emotions', label: 'Les émotions (tristesse, colère, peurs…)' },
    { id: 'confiance', label: 'La confiance en soi' },
    { id: 'relations', label: 'Les relations' },
    { id: 'autre', label: 'Autre' },
  ];

  return (
    <div className="space-y-6">
      {/* A. Informations générales */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="w-5 h-5 text-primary" />
            A. Informations générales
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type de consultant</Label>
              <RadioGroup
                value={data.consultant_type}
                onValueChange={(value) => updateField('consultant_type', value as 'adult' | 'child')}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="adult" id="adult" />
                  <Label htmlFor="adult" className="flex items-center gap-1 cursor-pointer">
                    <User className="w-4 h-4" /> Adulte
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="child" id="child" />
                  <Label htmlFor="child" className="flex items-center gap-1 cursor-pointer">
                    <Baby className="w-4 h-4" /> Enfant
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label>Type de séance</Label>
              <RadioGroup
                value={data.session_type}
                onValueChange={(value) => updateField('session_type', value as 'first' | 'followup')}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="first" id="first" />
                  <Label htmlFor="first" className="cursor-pointer">Première consultation</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="followup" id="followup" />
                  <Label htmlFor="followup" className="cursor-pointer">Suivi</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* B. Historique et suivi */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5 text-primary" />
            B. Historique et suivi
            <Badge variant="outline" className="ml-2 text-xs">Pré-rempli depuis questionnaire</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Émotion ou difficulté principale exprimée</Label>
            <Textarea
              value={data.emotion_principale}
              onChange={(e) => updateField('emotion_principale', e.target.value)}
              placeholder="Décrivez l'émotion ou difficulté principale..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Depuis combien de temps ?</Label>
            <div className="flex flex-wrap gap-2">
              {DUREE_OPTIONS.map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`duree-${option.id}`}
                    checked={data.duree_ressenti?.includes(option.id)}
                    onCheckedChange={() => toggleArrayField('duree_ressenti', option.id)}
                  />
                  <Label htmlFor={`duree-${option.id}`} className="text-sm cursor-pointer">
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Cette situation impacte principalement :</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {IMPACT_OPTIONS.map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`impact-${option.id}`}
                    checked={data.impact_principal?.includes(option.id)}
                    onCheckedChange={() => toggleArrayField('impact_principal', option.id)}
                  />
                  <Label htmlFor={`impact-${option.id}`} className="text-sm cursor-pointer">
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
            {data.impact_principal?.includes('autre') && (
              <Input
                value={data.impact_autre || ''}
                onChange={(e) => updateField('impact_autre', e.target.value)}
                placeholder="Précisez..."
                className="mt-2"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>A déjà utilisé les Fleurs de Bach</Label>
            <RadioGroup
              value={data.deja_utilise_bach ? 'oui' : 'non'}
              onValueChange={(value) => updateField('deja_utilise_bach', value === 'oui')}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="oui" id="bach-oui" />
                <Label htmlFor="bach-oui" className="cursor-pointer">Oui</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="non" id="bach-non" />
                <Label htmlFor="bach-non" className="cursor-pointer">Non</Label>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

      {/* C. Actualisation en début de consultation */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5 text-primary" />
            C. Actualisation en début de consultation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Depuis la prise de rendez-vous, l'état émotionnel est-il :</Label>
            <RadioGroup
              value={data.etat_emotionnel_evolution}
              onValueChange={(value) => updateField('etat_emotionnel_evolution', value as any)}
              className="flex flex-wrap gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="identique" id="evo-identique" />
                <Label htmlFor="evo-identique" className="cursor-pointer">Identique</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="evolue" id="evo-evolue" />
                <Label htmlFor="evo-evolue" className="cursor-pointer">A évolué</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="different" id="evo-different" />
                <Label htmlFor="evo-different" className="cursor-pointer">Différent aujourd'hui</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label>Notes d'actualisation</Label>
            <Textarea
              value={data.notes_actualisation || ''}
              onChange={(e) => updateField('notes_actualisation', e.target.value)}
              placeholder="Notez les évolutions depuis la prise de rendez-vous..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* D. Écoute et exploration émotionnelle */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Heart className="w-5 h-5 text-primary" />
            D. Écoute et exploration émotionnelle
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Émotions dominantes observées</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {EMOTIONS_DOMINANTES_OPTIONS.map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`emotion-${option.id}`}
                    checked={data.emotions_dominantes?.includes(option.id)}
                    onCheckedChange={() => toggleArrayField('emotions_dominantes', option.id)}
                  />
                  <Label htmlFor={`emotion-${option.id}`} className="text-sm cursor-pointer">
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
            {data.emotions_dominantes?.includes('autre') && (
              <Input
                value={data.emotion_autre || ''}
                onChange={(e) => updateField('emotion_autre', e.target.value)}
                placeholder="Précisez l'émotion..."
                className="mt-2"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>Manifestations / comportements observés</Label>
            <Textarea
              value={data.manifestations_comportements || ''}
              onChange={(e) => updateField('manifestations_comportements', e.target.value)}
              placeholder="Décrivez les manifestations observées..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Ressenti profond / ce qui pèse le plus</Label>
            <Textarea
              value={data.ressenti_profond || ''}
              onChange={(e) => updateField('ressenti_profond', e.target.value)}
              placeholder="Ce qui pèse le plus sur la personne..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={data.notes_exploration || ''}
              onChange={(e) => updateField('notes_exploration', e.target.value)}
              placeholder="Notes complémentaires..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* E. Objectif émotionnel */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="w-5 h-5 text-primary" />
            E. Objectif émotionnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Comment la personne aimerait-elle se sentir ?</Label>
            <Textarea
              value={data.objectif_emotionnel || ''}
              onChange={(e) => updateField('objectif_emotionnel', e.target.value)}
              placeholder="Décrivez l'objectif émotionnel souhaité..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* F. Sélection des Fleurs de Bach */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Flower2 className="w-5 h-5 text-primary" />
            F. Sélection des Fleurs de Bach
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Fleurs pressenties</Label>
            <Textarea
              value={data.fleurs_pressenties || ''}
              onChange={(e) => updateField('fleurs_pressenties', e.target.value)}
              placeholder="Notez les fleurs qui vous viennent à l'esprit..."
              rows={2}
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              Fleurs retenues (maximum 7)
              <Badge variant="secondary" className="text-xs">
                {Object.values(data.fleurs_retenues).filter(Boolean).length}/7
              </Badge>
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(['fleur1', 'fleur2', 'fleur3', 'fleur4', 'fleur5', 'fleur6', 'fleur7'] as const).map((key, index) => (
                <div key={key} className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Fleur {index + 1}</Label>
                  <Select
                    value={data.fleurs_retenues[key] ?? NONE_VALUE}
                    onValueChange={(value) => updateFleur(key, value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une fleur..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>Aucune</SelectItem>
                      {bachFlowers.map((flower) => (
                        <SelectItem key={flower.id} value={flower.name}>
                          <div className="flex items-center gap-2">
                            <Flower2 className="w-3 h-3" />
                            {flower.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Justification</Label>
            <Textarea
              value={data.justification_fleurs || ''}
              onChange={(e) => updateField('justification_fleurs', e.target.value)}
              placeholder="Expliquez le choix des fleurs..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* G. Flacon personnalisé */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FlaskConical className="w-5 h-5 text-primary" />
            G. Flacon personnalisé
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Flacon préparé</Label>
              <RadioGroup
                value={data.flacon_prepare ? 'oui' : 'non'}
                onValueChange={(value) => updateField('flacon_prepare', value === 'oui')}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="oui" id="flacon-oui" />
                  <Label htmlFor="flacon-oui" className="cursor-pointer">Oui</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="non" id="flacon-non" />
                  <Label htmlFor="flacon-non" className="cursor-pointer">Non</Label>
                </div>
              </RadioGroup>
            </div>

            {data.flacon_prepare && (
              <div className="space-y-2">
                <Label>Date de préparation</Label>
                <Input
                  type="date"
                  value={data.date_preparation || ''}
                  onChange={(e) => updateField('date_preparation', e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Posologie conseillée</Label>
              <RadioGroup
                value={data.posologie}
                onValueChange={(value) => updateField('posologie', value as any)}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="4gouttes4fois" id="poso-4x4" />
                  <Label htmlFor="poso-4x4" className="cursor-pointer">4 gouttes, 4 fois par jour</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="16gouttesJour" id="poso-16" />
                  <Label htmlFor="poso-16" className="cursor-pointer">16 gouttes à boire dans la journée</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label>Durée estimée</Label>
              <Input
                value={data.duree_estimee || ''}
                onChange={(e) => updateField('duree_estimee', e.target.value)}
                placeholder="Ex: 3 semaines"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* H. Observations praticien */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Stethoscope className="w-5 h-5 text-primary" />
            H. Observations praticien
            <Badge variant="outline" className="ml-2 text-xs">Confidentiel</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Suivi, vigilance, prochaine séance</Label>
            <Textarea
              value={data.observations_praticien || ''}
              onChange={(e) => updateField('observations_praticien', e.target.value)}
              placeholder="Notes confidentielles pour le suivi..."
              rows={4}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
